import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import {
    EstadoPendientePeriodo,
    OmitidosBackfillResult,
} from '../../models/bancario.model';

/** Envolvente estándar `{status, message, data}` de las respuestas de la API. */
interface ApiResponse<T> {
    /** Resultado de la operación: `success`, `partial` (éxito parcial) o `error`. */
    status: string;
    /** Descripción legible del resultado, apta para mostrar al usuario. */
    message: string;
    /** Carga útil de la respuesta. */
    data: T;
}

/**
 * Estados de cuenta sin mes determinable: sin fechas en filas, sin periodo
 * parseable y sin fecha de documento.
 *
 * Sus movimientos quedan OMITIDO y no
 * entran a conciliación ni reportes hasta que el usuario defina mes/año
 * (modal obligatorio en App).
 */
@Injectable({ providedIn: 'root' })
export class PeriodoPendienteService {
    /** Cliente HTTP. */
    private readonly http = inject(HttpClient);
    /** Base de los endpoints de estados de cuenta. */
    private readonly baseUrl = `${environment.apiUrl}/movimientos_bancarios/estados_cuenta`;

    /**
     * Lista los estados de cuenta que esperan que el usuario defina su periodo.
     * @returns Observable que emite los estados pendientes; vacío si no hay ninguno.
     */
    getPendientes(): Observable<EstadoPendientePeriodo[]> {
        return this.http
            .get<ApiResponse<EstadoPendientePeriodo[]>>(`${this.baseUrl}/pendientes_periodo`)
            .pipe(map((res) => res.data));
    }

    /**
     * Asigna mes y año a un estado de cuenta pendiente; el backend fecha sus movimientos omitidos y los reincorpora.
     *
     * Se conserva la envolvente porque la vista muestra el `message` del backend.
     * @param estadoId - Identificador del estado de cuenta.
     * @param mes - Mes del periodo (1-12).
     * @param anio - Año del periodo.
     * @returns Observable que emite el mensaje del backend y el resultado del backfill de omitidos.
     */
    definirPeriodo(
        estadoId: number,
        mes: number,
        anio: number,
    ): Observable<{ message: string; data: OmitidosBackfillResult }> {
        return this.http.patch<ApiResponse<OmitidosBackfillResult>>(
            `${this.baseUrl}/${estadoId}/definir_periodo`,
            { mes, anio },
        );
    }
}
